/**
 * Modo práctica: una tienda de mentira donde nada cuenta.
 *
 * The tutorial teaches by making people actually do the thing — open the till, ring up a sale,
 * hand back change, void it when they get it wrong. That only works if getting it wrong is
 * free, and in an append-only ledger nothing is free: a practice sale is a real sale, forever,
 * with real COGS posted against real stock.
 *
 * So practice runs against a different database. Not a different code path — a different
 * *database*, with the same name-plus-suffix trick the app already uses for everything else.
 * Every one of the thirty screens, every reducer, every projection works exactly as it does in
 * the real shop, because as far as they know it IS the shop. Nothing is mocked, so nothing can
 * teach the wrong lesson.
 *
 * Two rules keep it sealed:
 *
 *   1. The database name is decided here, before `dexie.ts` constructs anything, and it is
 *      read from localStorage rather than passed around — a module that imports `db` must not
 *      be able to get the wrong one depending on import order.
 *   2. Sync refuses to run. A practice sale that reached the server would be indistinguishable
 *      from a real one the moment it came back down, on every device in the shop.
 *
 * Entering and leaving both reload the page. That is not laziness: Dexie holds an open
 * connection and React holds a tree full of live queries against it, and swapping the
 * database underneath them is a much better way to produce a bug than a page load is to annoy
 * somebody once.
 */

const CLAVE = 'abastece.practica';

/** Both databases exist at once. The real one is never opened while practising. */
export const NOMBRE_REAL = 'abastece';
export const NOMBRE_PRACTICA = 'abastece.practica';

/**
 * Read once, at module load, and never re-read.
 *
 * `dexie.ts` asks for this while it is constructing the singleton, so it has to be a plain
 * synchronous value. Freezing it also means nothing can flip the flag mid-session and leave
 * half the app pointed at one database and half at the other.
 */
const ACTIVA: boolean = leerBandera();

function leerBandera(): boolean {
  try {
    return localStorage.getItem(CLAVE) === '1';
  } catch {
    // No storage means no practice mode, which is the safe way round: the worst case is that
    // somebody cannot start the tutorial, not that they practise into the real books.
    return false;
  }
}

/** Whether this page load is a practice session. Constant for the lifetime of the tab. */
export const enPractica = (): boolean => ACTIVA;

/** Which database this page load talks to. Called by `dexie.ts` and by nobody else. */
export const nombreBaseDatos = (): string => (ACTIVA ? NOMBRE_PRACTICA : NOMBRE_REAL);

/**
 * Set by the browser checks that drive the till's screens (`e2e/`), never by the app: the
 * practice shop is then built in the middle of the morning's shift, its till still open, rather
 * than at the start of the learner's, when it has just been counted and closed.
 */
const CLAVE_MEDIO_TURNO = 'abastece.practica.aMedioTurno';

export function aMedioTurno(): boolean {
  try {
    return localStorage.getItem(CLAVE_MEDIO_TURNO) === '1';
  } catch {
    return false;
  }
}

/**
 * Enter practice mode.
 *
 * Deletes any sandbox left over from last time first, so every session starts from the same
 * shop and a lesson can rely on what it finds. The reload is what actually switches over.
 */
export async function entrarAPractica(): Promise<void> {
  await borrarSandbox();
  // Verified, not assumed. If the flag does not stick, the reload lands on the REAL shop with
  // a tutorial driving it — practice sales in the real ledger, which is the one outcome this
  // module exists to prevent. Better to refuse to start.
  localStorage.setItem(CLAVE, '1');
  if (leerBandera() !== true) throw new Error('No se pudo entrar al modo práctica');
  location.assign('/');
}

/**
 * Leave practice mode and throw the sandbox away.
 *
 * The flag is cleared before the delete so that a failure half way through cannot strand
 * somebody inside a practice shop they have no way out of. A sandbox that survives is
 * harmless — the next entry wipes it.
 */
export async function salirDePractica(): Promise<void> {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* Nothing to do: the reload below still lands on the real database. */
  }
  await borrarSandbox();
  location.assign('/');
}

async function borrarSandbox(): Promise<void> {
  await new Promise<void>((listo) => {
    const req = indexedDB.deleteDatabase(NOMBRE_PRACTICA);
    // Resolve on every outcome. `blocked` fires when another tab still holds the database
    // open, and waiting forever for a tab nobody is looking at would hang the button.
    req.onsuccess = () => listo();
    req.onerror = () => listo();
    req.onblocked = () => listo();
  });
}
